<template>
  <AuthGuard>
    <v-container class="app-page-content pa-4 pa-md-6">
      <PageHeader
        title="Profile"
        :showBack="true"
        back-to="/customers"
        back-to-label="customers list"
        compact
      />

      <v-card class="customer-hero mb-6" rounded="lg" elevation="0" border>
        <v-card-text class="thor-profile-card-body">
          <div class="d-flex flex-nowrap align-center justify-space-between gap-4">
            <div class="d-flex align-center min-w-0 flex-grow-1 thor-profile-avatar-name-gap">
              <v-avatar color="primary" size="48" rounded="lg" variant="flat" class="shrink-0">
                <span class="text-h6 font-weight-bold text-white">{{ customerInitial }}</span>
              </v-avatar>
              <div class="min-w-0">
                <div class="text-h6 font-weight-bold text-truncate">
                  {{ customer?.name ?? "—" }}
                </div>
              </div>
            </div>
            <v-btn
              color="primary"
              variant="tonal"
              size="default"
              density="comfortable"
              prepend-icon="mdi-pencil-outline"
              class="text-transform-none shrink-0"
              @click="(e: Event) => handleClickEditCustomer(e, customerId)"
            >
              Edit name
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <div class="mb-4">
        <h2
          class="text-subtitle-1 font-weight-bold text-medium-emphasis text-uppercase letter-spacing-1"
        >
          Active & Paused data sources
        </h2>
      </div>

      <div class="sources-grid">
        <template v-if="isLoading">
          <v-sheet v-for="i in 3" :key="i" rounded="lg" border class="source-cell">
            <v-skeleton-loader class="bg-transparent" type="card" />
          </v-sheet>
        </template>

        <template v-else>
          <div
            v-for="account in activeAndPausedAccounts"
            :key="`${account.feed}-${account.account}`"
            class="source-cell"
          >
            <SourceCard
              :feed="account.feed"
              :account="account"
              :customer-name="customer?.name"
              :loading="isLoading"
              :user-id="user?.id!"
            />
          </div>
          <div class="source-cell">
            <SourceConnectCard :user-id="user?.id!" />
          </div>
        </template>
      </div>

      <div v-if="archivedAccounts.length > 0" class="mb-4 mt-8">
        <h2
          class="text-subtitle-1 font-weight-bold text-medium-emphasis text-uppercase letter-spacing-1"
        >
          Archived data sources
        </h2>
      </div>
      <div v-if="archivedAccounts.length > 0" class="sources-grid">
        <div
          v-for="account in archivedAccounts"
          :key="`archived-${account.feed}-${account.account}`"
          class="source-cell"
        >
          <SourceCard
            :feed="account.feed"
            :account="account"
            :customer-name="customer?.name"
            :loading="isLoading"
            :user-id="user?.id!"
          />
        </div>
      </div>
    </v-container>

    <v-dialog
      v-model="state.isPatchCustomerDialogActive"
      max-width="480"
      scrollable
      content-class="customer-dialog-overlay"
    >
      <v-card rounded="lg" elevation="12" class="customer-dialog-card">
        <v-card-title class="d-flex align-center justify-space-between px-5 pt-5 pb-2">
          <span class="text-h6 font-weight-bold">Edit customer name</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            density="comfortable"
            aria-label="Close"
            @click="state.isPatchCustomerDialogActive = false"
          />
        </v-card-title>
        <v-divider class="mx-0" />
        <v-card-text class="px-5 py-4">
          <CreateCustomerForm
            type="edit"
            :customer="state.customer"
            @cancel="state.isPatchCustomerDialogActive = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </AuthGuard>
</template>

<script setup lang="ts">
import PageHeader from "@/components/PageHeader.vue";
import { useAccountStore } from "@/stores/account";
import { computed, onMounted, reactive, watch } from "vue";
import { IApp } from "@/types";
import _ from "lodash";
import SourceCard from "@/components/Widgets/SourceCard.vue";
import SourceConnectCard from "@/components/Widgets/SourceConnectCard.vue";
import useRouteParams from "@/hooks/useRouteParams";
import { useCustomerStore } from "@/stores/customer";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import { useAuthStore } from "@/stores/auth";
import CreateCustomerForm from "@/components/Customer/CreateCustomerForm.vue";
import { isCustomerValid, makeAlert } from "@/utils";
import { URLs } from "@/constants";
import { useRouter } from "vue-router";

const authStore = useAuthStore();
const router = useRouter();
const user = computed(() => authStore.user);

const accountStore = useAccountStore();
const customerStore = useCustomerStore();

const { customerId } = useRouteParams();

const customer = computed(() => customerStore.getCustomer(customerId.value!));

const customerInitial = computed(() => {
  const n = customer.value?.name?.trim();
  if (!n) return "?";
  return n.charAt(0).toUpperCase();
});

const isCustomerAccessible = computed(() => customer.value && isCustomerValid(customer.value));

const activeAndPausedAccounts = computed(() =>
  _.filter(
    accountStore.accounts,
    (item) =>
      item.status === IApp.IAccountStatus.Active || item.status === IApp.IAccountStatus.Paused
  )
);

const archivedAccounts = computed(() =>
  _.filter(accountStore.accounts, (item) => item.status === IApp.IAccountStatus.Archived)
);

const state = reactive<{
  isPatchCustomerDialogActive: boolean;
  lastCustomerEditClickTimestamp: number;
  customer: IApp.ICustomer | null;
}>({
  isPatchCustomerDialogActive: false,
  lastCustomerEditClickTimestamp: 0,
  customer: null
});

const isLoading = computed(() => accountStore.accountsLoadingStatus == IApp.ILoadingStatus.Loading);

const handleClickEditCustomer = (e: Event, id: string | undefined) => {
  e.preventDefault();
  if (!id) return;
  state.customer = customerStore.getCustomer(id)!;
  state.isPatchCustomerDialogActive = true;
  state.lastCustomerEditClickTimestamp = Date.now();
};

watch(
  [customer, () => customerStore.customers.length],
  ([c, len]) => {
    if (len > 0 && customerId.value && (_.isUndefined(c) || !isCustomerValid(c!))) {
      makeAlert("This customer has been deleted and is no longer accessible", "error");
      router.push(URLs.PAGE_CUSTOMER_LIST);
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (user.value?.id && customerId.value && isCustomerAccessible.value) {
    accountStore.fetchAccountList(user.value?.id!, customerId.value);
  }
});
</script>

<style scoped>
.customer-hero {
  background: rgb(var(--v-theme-surface));
}

.thor-profile-card-body {
  padding: var(--thor-profile-card-padding);
}

/* 1 col default, 2 cols md (960px), 3 cols lg+ (1280px) — matches Vuetify breakpoints */
.sources-grid {
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 1fr);
}

@media (min-width: 960px) {
  .sources-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (min-width: 1280px) {
  .sources-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.source-cell {
  min-width: 0;
}

.source-cell :deep(.v-card) {
  height: 100%;
}
</style>
